import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../api/api-context';
import { SESSION_LIMITS, relativeDue, reviewRoute } from '../state/review-session';

function useReviewSummary(repertoireId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['review-summary', repertoireId, undefined, undefined],
    queryFn: () => api.getReviewSummary(repertoireId),
    retry: false,
  });
}

/** Compact "Practice · 12 due" link for a repertoire card. */
export function DueBadge({ repertoireId }: { repertoireId: string }): JSX.Element | null {
  const summary = useReviewSummary(repertoireId);
  if (!summary.data || summary.data.totalCount === 0) return null;
  const due = summary.data.dueCount;
  return (
    <Link
      className={`due-badge${due > 0 ? ' due-badge-active' : ''}`}
      to={reviewRoute(repertoireId, due > 0 ? { mode: 'due' } : {})}
    >
      {due > 0 ? `Practice · ${due} due` : 'Practice'}
    </Link>
  );
}

/** Practice entry point on the repertoire page: what is due and how to start. */
export function PracticePanel({ repertoireId }: { repertoireId: string }): JSX.Element | null {
  const summary = useReviewSummary(repertoireId);
  if (summary.isError) return null;
  const counts = summary.data;
  return (
    <section className="panel practice-panel" aria-labelledby="practice-title">
      <div>
        <h2 id="practice-title">Practice</h2>
        {!counts ? (
          <p className="muted" role="status">Loading your schedule…</p>
        ) : counts.totalCount === 0 ? (
          <p className="muted">Save some of your moves and they will show up here for practice.</p>
        ) : (
          <p className="muted">
            {counts.dueCount > 0
              ? `${counts.dueCount} of your ${counts.totalCount} moves ${counts.dueCount === 1 ? 'is' : 'are'} due.`
              : counts.nextDueAt
                ? `All caught up. Next review ${relativeDue(counts.nextDueAt)}.`
                : `${counts.totalCount} moves ready to learn.`}
            {counts.newCount > 0 && ` ${counts.newCount} not learned yet.`}
          </p>
        )}
      </div>
      {counts && counts.totalCount > 0 && (
        <div className="review-actions">
          {counts.dueCount > 0 && (
            <Link className="studio-primary" to={reviewRoute(repertoireId, { mode: 'due' })}>
              Review {counts.dueCount} due
            </Link>
          )}
          {counts.newCount > 0 && (
            <Link className={counts.dueCount > 0 ? 'button-link' : 'studio-primary'} to={reviewRoute(repertoireId, { mode: 'new' })}>
              Learn {Math.min(counts.newCount, SESSION_LIMITS.new)} new
            </Link>
          )}
          <Link className="button-link" to={reviewRoute(repertoireId)}>Practice options</Link>
        </div>
      )}
    </section>
  );
}
